<template>
  <v-container>
    <v-empty-state
      v-if="loaded && !photos.length"
      icon="mdi-webcam-off"
      size="64"
      title="Aucune photo pour le moment"
      text="La caméra n'a pas encore envoyé de photo. Revenez un peu plus tard."
    ></v-empty-state>
    <template v-else-if="photos.length">
      <v-alert v-if="isStale" type="warning" variant="tonal" density="compact" class="mb-4">
        La caméra n'a pas envoyé de nouvelle photo depuis le {{ formatDate(photos[0].taken_at) }}. Les conditions ont
        peut-être changé depuis.
      </v-alert>
      <v-row>
        <!-- Main viewer: zoom, pan and fullscreen on the selected photo -->
        <v-col ref="viewer" cols="12" md="8">
          <div class="camera-viewer">
            <zoomable-image
              :src="currentPhoto.url"
              :caption="caption(currentPhoto)"
              :has-previous="currentIndex > 0"
              :has-next="currentIndex < photos.length - 1"
              @previous="selectPhoto(currentIndex - 1)"
              @next="selectPhoto(currentIndex + 1)"
            ></zoomable-image>
          </div>
          <div class="d-flex align-start flex-wrap ga-2 mt-3">
            <p class="text-body-large mb-0 flex-grow-1">
              {{ formatDate(currentPhoto.taken_at) }}
              <span class="text-body-medium text-medium-emphasis">({{ formatAge(currentPhoto.taken_at) }})</span>
            </p>
            <span class="text-body-small text-medium-emphasis">
              Molette ou pincement pour zoomer, glisser pour se déplacer
            </span>
          </div>
        </v-col>
        <v-col cols="12" md="4">
          <div class="text-label-large text-medium-emphasis mb-2">
            {{ photos.length }} dernière{{ photos.length > 1 ? "s" : "" }} photo{{ photos.length > 1 ? "s" : "" }}
          </div>
          <v-list density="compact" class="py-0 camera-list">
            <v-list-item
              v-for="(photo, index) in photos"
              :key="photo.id"
              :active="index === currentIndex"
              color="primary"
              rounded="lg"
              class="mb-1 px-2"
              @click="selectPhoto(index, true)"
            >
              <template #prepend>
                <div class="camera-thumb mr-3">
                  <img :src="photo.url" alt="" loading="lazy" />
                </div>
              </template>
              <v-list-item-title class="text-body-medium">{{ formatDate(photo.taken_at) }}</v-list-item-title>
              <v-list-item-subtitle class="text-body-small">{{ formatAge(photo.taken_at) }}</v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
  import ZoomableImage from "@/components/ZoomableImage.vue";
  import { CamerasService } from "@/services/Cameras.service";
  import { formatPhotoAge, formatPhotoDate, isStalePhoto } from "@/utils/cameras";

  export default {
    name: "CameraPhotos",
    components: { ZoomableImage },
    props: {
      section: {
        type: Object,
        default: function () {
          return {};
        },
      },
    },
    data() {
      return {
        photos: [],
        currentIndex: 0,
        loaded: false,
      };
    },
    computed: {
      currentPhoto() {
        return this.photos[this.currentIndex] || this.photos[0];
      },
      isStale() {
        return this.photos.length > 0 && isStalePhoto(this.photos[0].taken_at);
      },
    },
    async mounted() {
      try {
        const camera = await CamerasService.getCamera(this.section.camera_id);
        this.photos = ((camera && camera.photos) || []).filter((photo) => photo && photo.url);
      } catch (error) {
        console.error("Error loading camera photos:", error);
      } finally {
        this.loaded = true;
      }
    },
    methods: {
      formatDate: formatPhotoDate,
      formatAge(value) {
        return formatPhotoAge(value);
      },
      caption(photo) {
        return photo ? `Photo du ${formatPhotoDate(photo.taken_at)}` : "";
      },
      selectPhoto(index, fromList = false) {
        this.currentIndex = index;
        // On small screens the list is below the viewer: bring the viewer back into view
        if (fromList && this.$vuetify.display.smAndDown && this.$refs.viewer) {
          this.$refs.viewer.$el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .camera-viewer {
    height: 70vh;
    min-height: 320px;
  }
  .camera-list {
    max-height: 70vh;
    overflow-y: auto;
  }
  .camera-thumb {
    flex: 0 0 auto;
    width: 112px;
    aspect-ratio: 4 / 3;
    border-radius: 6px;
    overflow: hidden;
    background: rgb(var(--v-theme-surface-variant));

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
</style>
